import { useEffect, useState } from "react";
import { CheckCircle, InformationCircle, Warning, XCircle, Close } from "./icons";
import { cn } from "packages/dsaui/src/utils";
import { useNotification } from "utils/hooks";

export type NotificationType = "error" | "warning" | "info" | "success";

export interface NotificationProps {
  message?: string;
  variant?: NotificationType;
  timeout?: number;
}

const notificationStyles = {
  error: {
    container: "border-red-200 bg-red-50 text-red-900",
    icon: "text-red-600",
    close: "text-red-700 hover:text-red-900",
  },
  warning: {
    container: "border-yellow-200 bg-yellow-50 text-yellow-900",
    icon: "text-yellow-600",
    close: "text-yellow-700 hover:text-yellow-900",
  },
  info: {
    container: "border-sky-200 bg-sky-50 text-sky-900",
    icon: "text-sky-600",
    close: "text-sky-700 hover:text-sky-900",
  },
  success: {
    container: "border-green-200 bg-green-50 text-green-900",
    icon: "text-green-600",
    close: "text-green-700 hover:text-green-900",
  },
} as const;

const notificationIcons = {
  error: XCircle,
  warning: Warning,
  info: InformationCircle,
  success: CheckCircle,
} as const;

const Notification = () => {
  const defaultTimeout = 4500;
  const { notification } = useNotification();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!notification?.message) return;

    //TODO investigate setState in useEffect
    setVisible(true); // eslint-disable-line react-hooks/set-state-in-effect
    const timer = setTimeout(() => {
      setVisible(false);
    }, notification?.timeout ?? defaultTimeout);

    return () => clearTimeout(timer);
  }, [notification]);

  if (!notification?.message) return null;

  const variant = notification.variant ?? "info";
  const styles = notificationStyles[variant];
  const Icon = notificationIcons[variant];

  return (
    <div
      className={cn(
        "fixed inset-x-4 top-4 z-50 transition-all duration-200 sm:right-auto sm:left-1/2 sm:max-w-md sm:-translate-x-1/2",
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-6 opacity-0",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-3 rounded-md border p-4 shadow-lg",
          styles.container,
        )}
        role="alert"
      >
        <Icon className={cn("size-5 shrink-0", styles.icon)} />
        <p className="text-label-sm m-0 min-w-0 flex-1 wrap-anywhere">
          {notification.message}
        </p>
        <button
          aria-label="Dismiss notification"
          className={cn(
            "cursor-pointer border-0 bg-transparent p-0 focus:ring-2 focus:ring-offset-2 focus:outline-none",
            styles.close,
          )}
          onClick={() => setVisible(false)}
          type="button"
        >
          <Close className="block size-5" />
        </button>
      </div>
    </div>
  );
};

export default Notification;
